import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { workingHoursAPI } from "../../../services/calendarService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faExclamationTriangle, faCheck } from "@fortawesome/free-solid-svg-icons";
import Heading from "../../../components/Common/Heading/Heading";
import { useBreadcrumb } from "../../../contexts/BreadcrumbContext";
import "../../../components/Common/ListScreenPageLayout/listScreenPage.css";
import "./WorkingHours.css";

const NOON_MINUTES = 12 * 60;

const WorkingHours = () => {
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    practice_id: null
  });
  const [weekDays, setWeekDays] = useState([
    { key: "0", short_name: "Sun", name: "Sunday" },
    { key: "1", short_name: "Mon", name: "Monday" },
    { key: "2", short_name: "Tue", name: "Tuesday" },
    { key: "3", short_name: "Wed", name: "Wednesday" },
    { key: "4", short_name: "Thu", name: "Thursday" },
    { key: "5", short_name: "Fri", name: "Friday" },
    { key: "6", short_name: "Sat", name: "Saturday" }
  ]);
  const [selectedDays, setSelectedDays] = useState({});
  const [timeRanges, setTimeRanges] = useState({});
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const selectedWeekDays = useMemo(
    () => Object.keys(selectedDays).filter(day => selectedDays[day]),
    [selectedDays]
  );

  useEffect(() => {
    updateBreadcrumbs([
      { label: "Calendar Settings", path: "/calendar-settings/visit-type" },
      { label: "Working Hours", path: null, active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  const parseTimeToMinutes = (value) => {
    if (!value || typeof value !== "string" || !value.includes(":")) return null;
    const [hh, mm] = value.split(":").map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  };

  // Represents a time range on a 24-hour clock, including overnight spans.
  const toSegments = (startMin, endMin) => {
    if (startMin === endMin) return [];
    if (startMin < endMin) return [{ s: startMin, e: endMin }];
    return [
      { s: startMin, e: 1439 },
      { s: 0, e: endMin }
    ];
  };

  // Inclusive overlap check so touching boundary times are treated as invalid.
  const hasOverlapInclusive = (rangeA, rangeB) => {
    const segA = toSegments(rangeA.start, rangeA.end);
    const segB = toSegments(rangeB.start, rangeB.end);
    return segA.some((a) => segB.some((b) => !(a.e < b.s || b.e < a.s)));
  };

  const buildDayRangeErrors = (ranges = []) => {
    const dayErrors = {};
    const parsed = [];

    ranges.forEach((r, index) => {
      const start = parseTimeToMinutes(r.start_time);
      const end = parseTimeToMinutes(r.end_time);

      // Real-time validation: if only start is entered, ensure it does not fall inside an existing saved range.
      if (start !== null && end === null) {
        const startConflicts = ranges.some((other, otherIndex) => {
          if (otherIndex === index) return false;
          const os = parseTimeToMinutes(other.start_time);
          const oe = parseTimeToMinutes(other.end_time);
          if (os === null || oe === null || os === oe) return false;
          const otherSegments = toSegments(os, oe);
          return otherSegments.some((seg) => start >= seg.s && start <= seg.e);
        });
        if (startConflicts) {
          dayErrors[index] = { startMsg: "Selected time is unavailable." };
        }
        return;
      }

      if (start === null || end === null) return;
      if (start === end) {
        const msg = "Start and end time cannot be the same.";
        dayErrors[index] = { startMsg: msg, endMsg: msg };
        return;
      }
      // Same calendar evening: end before start (e.g. 6:00 PM end 5:00 PM) — not a valid overnight span
      if (start > end && end > NOON_MINUTES) {
        dayErrors[index] = { endMsg: "End time must be after start time." };
        return;
      }
      parsed.push({ index, start, end });
    });

    for (let i = 0; i < parsed.length; i += 1) {
      for (let j = i + 1; j < parsed.length; j += 1) {
        if (!hasOverlapInclusive(parsed[i], parsed[j])) continue;
        const msg = "Selected time is unavailable.";
        const targetIndex = Math.max(parsed[i].index, parsed[j].index);
        dayErrors[targetIndex] = { startMsg: msg, endMsg: msg };
      }
    }

    return dayErrors;
  };

  const mergeSpillErrors = (rangesMap, selDays, daysList) => {
    const spillOut = {};
    const shorts = daysList.map((d) => d.short_name);
    shorts.forEach((dayShort, dayIdx) => {
      if (!selDays[dayShort]) return;
      const ranges = rangesMap[dayShort] || [];
      ranges.forEach((r, index) => {
        const s = parseTimeToMinutes(r.start_time);
        const e = parseTimeToMinutes(r.end_time);
        if (s === null || e === null) return;
        if (s <= e) return;
        if (e > NOON_MINUTES) return;
        const nextShort = shorts[(dayIdx + 1) % 7];
        if (!selDays[nextShort]) {
          if (!spillOut[dayShort]) spillOut[dayShort] = {};
          spillOut[dayShort][index] = {
            startMsg: "Overnight hours extend into the next day. Select that day or shorten this range."
          };
          return;
        }
        const spillSegs = toSegments(0, e);
        const nextRanges = rangesMap[nextShort] || [];
        nextRanges.forEach((nr) => {
          const ns = parseTimeToMinutes(nr.start_time);
          const ne = parseTimeToMinutes(nr.end_time);
          if (ns === null || ne === null || ns === ne) return;
          const segs = toSegments(ns, ne);
          spillSegs.forEach((sp) => {
            segs.forEach((seg) => {
              if (!(sp.e < seg.s || seg.e < sp.s)) {
                if (!spillOut[dayShort]) spillOut[dayShort] = {};
                spillOut[dayShort][index] = {
                  startMsg: "Overnight hours overlap the next day's schedule."
                };
              }
            });
          });
        });
      });
    });
    return spillOut;
  };

  const computeAllSlotErrors = (rangesMap, selDays) => {
    const active = Object.keys(selDays).filter((d) => selDays[d]);
    const out = {};
    active.forEach((day) => {
      const de = buildDayRangeErrors(rangesMap[day] || []);
      if (Object.keys(de).length) out[day] = { ...de };
    });
    const spill = mergeSpillErrors(rangesMap, selDays, weekDays);
    Object.keys(spill).forEach((day) => {
      if (!out[day]) out[day] = {};
      Object.keys(spill[day]).forEach((idx) => {
        const i = parseInt(idx, 10);
        const prev = out[day][i] || {};
        const sp = spill[day][i];
        out[day][i] = {
          startMsg: [prev.startMsg, sp.startMsg].filter(Boolean).join(" ").trim() || undefined,
          endMsg: [prev.endMsg, sp.endMsg].filter(Boolean).join(" ").trim() || undefined
        };
        if (!out[day][i].startMsg && !out[day][i].endMsg) delete out[day][i];
      });
    });
    return out;
  };

  const normalizeSlotFieldErrors = (slotEntry) => {
    if (!slotEntry) return { startMsg: null, endMsg: null };
    if (slotEntry.startMsg != null || slotEntry.endMsg != null) {
      return { startMsg: slotEntry.startMsg || null, endMsg: slotEntry.endMsg || null };
    }
    const m = slotEntry.message;
    if (!m) return { startMsg: null, endMsg: null };
    if (slotEntry.start_time && slotEntry.end_time) return { startMsg: m, endMsg: m };
    if (slotEntry.end_time && !slotEntry.start_time) return { startMsg: null, endMsg: m };
    if (slotEntry.start_time) return { startMsg: m, endMsg: null };
    return { startMsg: null, endMsg: null };
  };

  useEffect(() => {
    if (loading) return;
    const slot = computeAllSlotErrors(timeRanges, selectedDays);
    setErrors((prev) => ({
      start_date: prev.start_date,
      week_days: prev.week_days,
      ...slot
    }));
  }, [timeRanges, selectedDays, weekDays, loading]);

  const fetchWorkingHours = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await workingHoursAPI.getWorkingHours();
      if (response.success) {
        const { schedule, practiceId, availability, weekDays: weekDaysData } = response.data;

        setFormData({
          start_date: schedule.start_date || "",
          end_date: schedule.end_date || "",
          practice_id: practiceId
        });

        // Use weekDaysData from backend if available, otherwise use default
        const defaultWeekDays = [
          { key: "0", short_name: "Sun", name: "Sunday" },
          { key: "1", short_name: "Mon", name: "Monday" },
          { key: "2", short_name: "Tue", name: "Tuesday" },
          { key: "3", short_name: "Wed", name: "Wednesday" },
          { key: "4", short_name: "Thu", name: "Thursday" },
          { key: "5", short_name: "Fri", name: "Friday" },
          { key: "6", short_name: "Sat", name: "Saturday" }
        ];

        const currentWeekDays = weekDaysData || defaultWeekDays;
        if (weekDaysData) {
          setWeekDays(weekDaysData);
        }

        // Initialize time ranges from availability
        const initialRanges = {};
        const initialSelected = {};

        currentWeekDays.forEach(day => {
          initialRanges[day.short_name] = [];
          initialSelected[day.short_name] = false;
        });

        if (availability) {
          Object.keys(availability).forEach(dayKey => {
            const day = currentWeekDays.find(d => d.key === dayKey);
            if (day && availability[dayKey]) {
              initialRanges[day.short_name] = availability[dayKey].map(avail => ({
                id: avail.id,
                start_time: avail.start_time || "09:00",
                end_time: avail.end_time || "17:00"
              }));
              initialSelected[day.short_name] = true;
            }
          });
        }

        // If no availability, set default for each day
        currentWeekDays.forEach(day => {
          if (initialRanges[day.short_name].length === 0) {
            initialRanges[day.short_name] = [{
              id: null,
              start_time: "09:00",
              end_time: "17:00"
            }];
          }
        });

        setTimeRanges(initialRanges);
        setSelectedDays(initialSelected);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch working hours");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []); // Remove weekDays from dependencies to prevent infinite loop

  useEffect(() => {
    fetchWorkingHours();
  }, [fetchWorkingHours]);

  const toggleDay = (dayName) => {
    setSelectedDays(prev => ({
      ...prev,
      [dayName]: !prev[dayName]
    }));
  };

  const addTimeRange = (dayName) => {
    setTimeRanges((prev) => ({
      ...prev,
      [dayName]: [
        ...(prev[dayName] || []),
        { id: null, start_time: "", end_time: "" }
      ]
    }));
  };

  const removeTimeRange = (dayName, index, rangeId) => {
    if (timeRanges[dayName].length === 1) {
      toast.warning("At least one time range is required per day");
      return;
    }

    // Unsaved row added via "+" (no id) should be removed immediately without confirmation modal.
    if (!rangeId) {
      setTimeRanges((prev) => ({
        ...prev,
        [dayName]: prev[dayName].filter((_, i) => i !== index)
      }));
      return;
    }

    setPendingDelete({ dayName, index, rangeId });
    setShowDeleteConfirmModal(true);
  };

  const confirmRemoveTimeRange = () => {
    if (!pendingDelete) return;
    const { dayName, index } = pendingDelete;
    setTimeRanges((prev) => ({
      ...prev,
      [dayName]: prev[dayName].filter((_, i) => i !== index)
    }));
    setShowDeleteConfirmModal(false);
    setPendingDelete(null);
    toast.success("Time entry deleted successfully");
  };

  const updateTimeRange = (dayName, index, field, value) => {
    setTimeRanges((prev) => {
      const currentRanges = [...(prev[dayName] || [])];
      if (field === "end_time" && value) {
        const startStr = currentRanges[index]?.start_time;
        if (parseTimeToMinutes(startStr) === null) {
          toast.warning("Please select a start time first.");
          return prev;
        }
      }
      const newRanges = [...currentRanges];
      newRanges[index] = {
        ...newRanges[index],
        [field]: value
      };
      return {
        ...prev,
        [dayName]: newRanges
      };
    });
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    // Validate timings
    if (selectedWeekDays.length === 0) {
      newErrors.week_days = "Please select at least one day";
    }

    // Prepare data for validation
    const validationData = {
      start_date: formData.start_date,
      end_date: formData.end_date,
      week_days: selectedWeekDays,
      start_time: {},
      end_time: {}
    };

    selectedWeekDays.forEach(day => {
      validationData.start_time[day] = timeRanges[day]?.map(r => r.start_time) || [];
      validationData.end_time[day] = timeRanges[day]?.map(r => r.end_time) || [];
    });

    const slotErr = computeAllSlotErrors(timeRanges, selectedDays);
    Object.keys(slotErr).forEach((day) => {
      newErrors[day] = slotErr[day];
    });

    selectedWeekDays.forEach((day) => {
      const ranges = timeRanges[day] || [];
      ranges.forEach((r, index) => {
        const start = parseTimeToMinutes(r.start_time);
        const end = parseTimeToMinutes(r.end_time);
        if (start === null || end === null) {
          if (!newErrors[day]) newErrors[day] = {};
          const msg = "Please select both start and end time.";
          newErrors[day][index] = { startMsg: msg, endMsg: msg };
        }
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    // Validate with backend
    try {
      const response = await workingHoursAPI.validateWorkingHours(validationData);
      if (!response.success) {
        if (response.errors) {
          setErrors({ ...newErrors, ...response.errors });
          return false;
        }
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors({ ...newErrors, ...error.response.data.errors });
        return false;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!(await validateForm())) {
      toast.error("Please fix the errors before saving");
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        practice_id: formData.practice_id,
        week_days: selectedWeekDays,
        start_time: {},
        end_time: {}
      };

      selectedWeekDays.forEach(day => {
        submitData.start_time[day] = timeRanges[day]?.map(r => r.start_time) || [];
        submitData.end_time[day] = timeRanges[day]?.map(r => r.end_time) || [];
      });

      const response = await workingHoursAPI.storeWorkingHours(submitData);
      if (response.success) {
        toast.success("Working hours updated successfully");
        fetchWorkingHours({ silent: true });
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors((prev) => ({ ...prev, ...error.response.data.errors }));
        toast.error("Please fix the validation errors");
      } else {
        toast.error(error.message || "Failed to save working hours");
      }
    } finally {
      setSaving(false);
    }
  };

  const hasBlockingSlotErrors = useMemo(() => {
    const slot = computeAllSlotErrors(timeRanges, selectedDays);
    const hasErr = Object.keys(slot).some((d) =>
      Object.keys(slot[d] || {}).some((idx) => {
        const n = slot[d][idx];
        return !!(n?.startMsg || n?.endMsg);
      })
    );
    const incomplete = selectedWeekDays.some((day) =>
      (timeRanges[day] || []).some((r) => {
        const s = parseTimeToMinutes(r.start_time);
        const e = parseTimeToMinutes(r.end_time);
        return s === null || e === null;
      })
    );
    const serverSlotErrors = selectedWeekDays.some((day) =>
      Object.keys(errors[day] || {}).some((idx) => {
        const n = errors[day]?.[idx];
        const parsed = normalizeSlotFieldErrors(n);
        return !!(parsed.startMsg || parsed.endMsg);
      })
    );
    return hasErr || incomplete || serverSlotErrors;
  }, [timeRanges, selectedDays, selectedWeekDays, weekDays, errors]);

  const getSlotFieldErrors = (dayShort, index) => {
    return normalizeSlotFieldErrors(errors[dayShort]?.[index]);
  };

  return (
    <div className="working-hours-feature list-screen-page-container">
      <div className="working-hours-header list-screen-top-bar">
        <Heading title="Working Hours" level="h2" className="page-title list-screen-title-text" />
      </div>

      <fieldset className="working-hours-fieldset">
        <legend className="working-hours-legend">Office Hours</legend>

        <form onSubmit={handleSubmit} className="working-hours-form">
          <div className="date-row">
            <div className="form-group">
              <label>
                Start Date <span className="required">*</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                onClick={(e) => { try { e.target.showPicker(); } catch (_) {} }}
                className={errors.start_date ? "error" : ""}
              />
              {errors.start_date && (
                <span className="error-message">{errors.start_date}</span>
              )}
            </div>

            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData({ ...formData, end_date: e.target.value })
                }
                min={formData.start_date}
                onClick={(e) => { try { e.target.showPicker(); } catch (_) {} }}
              />
            </div>
          </div>

          {errors.week_days && (
            <div className="error-message">{errors.week_days}</div>
          )}

          <div className="working-hours-table-container">
            <div className="reusable-table-container">
              <div className="reusable-table-responsive">
                <table className="reusable-table working-hours-table">
              <colgroup>
                <col className="col-day" />
                <col className="col-start" />
                <col className="col-end" />
                <col className="col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Days</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? [...Array(8)].map((_, idx) => (
                    <tr key={`wh-skeleton-${idx}`}>
                      <td colSpan="4">
                        <div className="working-hours-skeleton-row">
                          <span className="working-hours-skeleton-cell short" />
                          <span className="working-hours-skeleton-cell" />
                          <span className="working-hours-skeleton-cell" />
                          <span className="working-hours-skeleton-cell short" />
                        </div>
                      </td>
                    </tr>
                  ))
                  : weekDays.map((day) => {
                  const dayRanges = timeRanges[day.short_name] || [];
                  const isSelected = selectedDays[day.short_name] || false;

                  return dayRanges.map((range, index) => {
                    const slotErr = getSlotFieldErrors(day.short_name, index);
                    return (
                    <tr key={`${day.short_name}-${index}`} className={day.short_name}>
                      {index === 0 && (
                        <>
                          <td rowSpan={dayRanges.length}>
                            <label className="day-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleDay(day.short_name)}
                              />
                              {day.short_name}
                            </label>
                          </td>
                        </>
                      )}
                      <td className="time-input-cell">
                        <input
                          type="time"
                          value={range.start_time || ""}
                          onChange={(e) =>
                            updateTimeRange(day.short_name, index, "start_time", e.target.value)
                          }
                          onClick={(e) => { try { e.target.showPicker(); } catch (_) {} }}
                          className={slotErr.startMsg ? "error" : ""}
                        />
                        {slotErr.startMsg && (
                          <div className="error-message-small">{slotErr.startMsg}</div>
                        )}
                      </td>
                      <td className="time-input-cell">
                        <input
                          type="time"
                          value={range.end_time || ""}
                          onChange={(e) =>
                            updateTimeRange(day.short_name, index, "end_time", e.target.value)
                          }
                          onClick={(e) => { try { e.target.showPicker(); } catch (_) {} }}
                          className={slotErr.endMsg ? "error" : ""}
                        />
                        {slotErr.endMsg && (
                          <div className="error-message-small">{slotErr.endMsg}</div>
                        )}
                      </td>
                      <td className="action-cell">
                        <div className="action-btn-wrap">
                          {index === 0 && (
                            <button
                              type="button"
                              className="add-time-btn"
                              onClick={() => addTimeRange(day.short_name)}
                              title="Add time range"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          )}
                          {index > 0 && (
                            <button
                              type="button"
                              className="remove-time-btn"
                              onClick={() => removeTimeRange(day.short_name, index, range.id)}
                              title="Remove time range"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  });
                })}
              </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="save-btn" disabled={saving || hasBlockingSlotErrors}>
              <FontAwesomeIcon icon={faCheck} className="me-1" />
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </fieldset>

      {showDeleteConfirmModal && (
        <div className="working-hours-delete-overlay" onClick={() => setShowDeleteConfirmModal(false)}>
          <div className="working-hours-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="working-hours-delete-icon-wrap">
              <FontAwesomeIcon icon={faExclamationTriangle} className="working-hours-delete-icon" />
            </div>
            <h4 className="working-hours-delete-title">Are you sure?</h4>
            <p className="working-hours-delete-text">
              You won't be able to revert it!
            </p>
            <div className="working-hours-delete-actions">
              <button
                type="button"
                className="working-hours-delete-btn working-hours-delete-btn-cancel"
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="working-hours-delete-btn working-hours-delete-btn-danger"
                onClick={confirmRemoveTimeRange}
              >
                Yes, delete it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingHours;

